/* eslint-disable react/no-unescaped-entities */
/**
 * ExpertPhotoStudio — Studio Photo Professionnelle assistée par IA.
 *
 * Flux :
 *   1) Capture caméra ou import fichier  → upload
 *   2) Choix d'un style (4 propositions) → génération IA (Nano Banana)
 *   3) Aperçu avant/après               → recommencer OU valider
 *   4) Consentement RGPD obligatoire     → publication sur la fiche
 *
 * Garde-fous front : le bouton "Publier" est désactivé tant que la case
 * de consentement n'est pas cochée. La photo source est purgée
 * automatiquement côté serveur dès la validation.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera, Upload, Sparkles, ArrowLeft, RefreshCw, ShieldCheck, Trash2,
  Loader2, CheckCircle2, AlertCircle, Image as ImageIcon, X,
} from "lucide-react";

const STYLES = [
  { key: "white",   label: "Fond blanc professionnel",   desc: "Studio clair, neutre, institutionnel.",      swatch: "#FFFFFF", border: true },
  { key: "navy",    label: "Fond bleu CONFILEX",          desc: "Bleu marine sobre, institutionnel.",         swatch: "#0B3A2A" },
  { key: "office",  label: "Bureau / cabinet flouté",     desc: "Décor de cabinet, profondeur de champ.",     swatch: "linear-gradient(135deg, #8E7A52 0%, #C9A961 60%, #0B3A2A 100%)" },
  { key: "premium", label: "Portrait premium sobre",      desc: "Fond ivoire premium, ambiance éditoriale.",  swatch: "#FAF7F1", border: true },
];

export default function ExpertPhotoStudio() {
  const [state, setState] = useState({
    has_original: false, generated_styles: [], has_final: false,
    published_photo: null, available_styles: [],
  });
  const [origPreview, setOrigPreview] = useState(null);     // dataURL local
  const [generated, setGenerated] = useState({});           // { style: dataURL }
  const [selectedStyle, setSelectedStyle] = useState("white");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState("upload"); // upload | choose | preview
  const [consent, setConsent] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraMode, setCameraMode] = useState(null); // "photo" | "video"
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const loadState = async () => {
    try {
      const { data } = await apiClient.get("/expert/photo");
      setState(data);
      if (data.has_final) setStep("preview");
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadState(); }, []);

  // -------- Camera ----------
  // Pattern : on monte d'abord le <video> (via setCameraOn), PUIS un useEffect
  // attache le MediaStream une fois l'élément présent dans le DOM. Ça évite le
  // bug "vidéo noire" où videoRef.current === null au moment du srcObject.
  const startCamera = async (mode = "photo") => {
    setCameraError(null);
    setCameraReady(false);
    setRecordedBlobUrl(null);
    setRecordedBlob(null);
    setCameraMode(mode);
    setCameraOn(true);
  };

  const stopCamera = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
    setCameraReady(false);
    setCameraMode(null);
    setRecording(false);
  };
  useEffect(() => () => stopCamera(), []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Une fois <video> monté ET cameraOn=true, on demande le flux puis l'attache.
  useEffect(() => {
    if (!cameraOn) return;
    if (!videoRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const constraints = {
          video: {
            facingMode: "user",
            width:  { ideal: 1280 },
            height: { ideal: 1280 },
          },
          // L'audio n'est requis que pour la vidéo (sinon iOS Safari le refuse parfois)
          audio: cameraMode === "video",
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;

        const v = videoRef.current;
        if (!v) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        v.srcObject = stream;

        // iOS Safari : il faut appeler play() explicitement après srcObject
        const onReady = () => {
          if (cancelled) return;
          if (v.videoWidth > 0 && v.videoHeight > 0) {
            setCameraReady(true);
          }
        };
        v.onloadedmetadata = onReady;
        v.oncanplay = onReady;
        try { await v.play(); } catch (e) { /* iOS sometimes throws */ }
      } catch (e) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[ExpertPhotoStudio] getUserMedia failed", e);
        let msg = "L'accès caméra ou micro est nécessaire pour utiliser le studio photo/vidéo.";
        if (e && e.name === "NotAllowedError") {
          msg = "Permission refusée. Autorisez l'accès caméra/micro dans les paramètres de votre navigateur.";
        } else if (e && e.name === "NotFoundError") {
          msg = "Aucune caméra n'a été détectée sur cet appareil.";
        } else if (e && e.name === "NotReadableError") {
          msg = "La caméra est déjà utilisée par une autre application.";
        }
        setCameraError(msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cameraOn, cameraMode]);

  const capturePhoto = async () => {
    if (!videoRef.current || !cameraReady) {
      toast.error("Caméra en cours de chargement, veuillez patienter.");
      return;
    }
    const v = videoRef.current;
    const vw = v.videoWidth;
    const vh = v.videoHeight;
    if (!vw || !vh) {
      toast.error("Flux vidéo non disponible. Réessayez dans quelques secondes.");
      return;
    }
    const size = Math.min(vw, vh);
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    // Centre crop carré
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);

    canvas.toBlob(async (blob) => {
      if (!blob || blob.size < 1000) {
        toast.error("Capture invalide (image vide). Réessayez.");
        return;
      }
      const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
      await uploadFile(file);
      stopCamera();
    }, "image/jpeg", 0.92);
  };

  // -------- Vidéo (MediaRecorder) ----------
  const pickSupportedMimeType = () => {
    if (typeof MediaRecorder === "undefined") return null;
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4;codecs=h264,aac",   // Safari iOS 14.5+
      "video/mp4",
    ];
    for (const t of candidates) {
      try {
        if (MediaRecorder.isTypeSupported(t)) return t;
      } catch (e) { /* ignore */ }
    }
    return null;
  };

  const startVideoRecording = () => {
    if (!streamRef.current || !cameraReady) {
      toast.error("Caméra en cours de chargement, veuillez patienter.");
      return;
    }
    const mime = pickSupportedMimeType();
    if (!mime) {
      toast.error("Enregistrement vidéo non supporté", {
        description: "Votre navigateur ne supporte pas l'enregistrement. Utilisez l'option d'import manuel.",
      });
      return;
    }
    try {
      recordedChunksRef.current = [];
      const recorder = new MediaRecorder(streamRef.current, { mimeType: mime });
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mime });
        if (blob.size < 1000) {
          toast.error("Vidéo trop courte ou vide.");
          return;
        }
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedBlobUrl(url);
      };
      recorder.start(1000); // chunk toutes les secondes
      recorderRef.current = recorder;
      setRecording(true);
      setRecordedDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordedDuration((d) => {
          if (d >= 30) { stopVideoRecording(); return 30; }  // cap 30 s
          return d + 1;
        });
      }, 1000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("MediaRecorder error", e);
      toast.error("Impossible de démarrer l'enregistrement.");
    }
  };

  const stopVideoRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecording(false);
  };

  const resetVideoRecording = () => {
    if (recordedBlobUrl) URL.revokeObjectURL(recordedBlobUrl);
    setRecordedBlob(null);
    setRecordedBlobUrl(null);
    setRecordedDuration(0);
  };

  const uploadRecordedVideo = async () => {
    if (!recordedBlob) return;
    setBusy(true);
    try {
      const ext = recordedBlob.type.includes("mp4") ? "mp4" : "webm";
      const file = new File([recordedBlob], `studio-video.${ext}`, { type: recordedBlob.type });
      const fd = new FormData();
      fd.append("file", file);
      await apiClient.post("/expert/photo/video/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Vidéo enregistrée sur votre profil spécialiste.");
      resetVideoRecording();
      stopCamera();
      await loadState();
    } catch (e) {
      toast.error("Upload vidéo impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onVideoFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Vidéo trop volumineuse", { description: "Max 50 Mo." });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await apiClient.post("/expert/photo/video/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Vidéo importée");
      await loadState();
    } catch (err) {
      toast.error("Upload impossible", { description: formatApiError(err?.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  // -------- Upload ---------
  const uploadFile = async (file) => {
    setBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = () => setOrigPreview(reader.result);
      reader.readAsDataURL(file);
      const fd = new FormData();
      fd.append("file", file);
      await apiClient.post("/expert/photo/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo source chargée");
      setStep("choose");
      setGenerated({});
      await loadState();
    } catch (e) {
      toast.error("Upload impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadFile(f);
  };

  // -------- Generate IA ---------
  const generate = async (styleKey) => {
    setGenerating(true);
    setSelectedStyle(styleKey);
    try {
      const { data } = await apiClient.post("/expert/photo/generate", { style: styleKey });
      setGenerated(prev => ({ ...prev, [styleKey]: data.image_base64 }));
      setStep("preview");
      toast.success(`Style "${STYLES.find(s => s.key === styleKey)?.label}" généré (${(data.duration_ms / 1000).toFixed(1)}s)`);
    } catch (e) {
      toast.error("Génération impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setGenerating(false); }
  };

  // -------- Validate ---------
  const validate = async () => {
    if (!consent) { toast.error("Cochez d'abord la case de consentement"); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/expert/photo/validate", {
        style: selectedStyle, consent: true,
      });
      toast.success("Photo publiée sur votre fiche CONFILEX");
      await loadState();
      setGenerated({});
      setOrigPreview(null);
      setStep("preview");
    } catch (e) {
      toast.error("Publication impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const deletePhoto = async () => {
    if (!window.confirm("Supprimer définitivement votre photo professionnelle ?")) return;
    setBusy(true);
    try {
      await apiClient.delete("/expert/photo");
      toast.success("Photo supprimée");
      setOrigPreview(null);
      setGenerated({});
      setConsent(false);
      setStep("upload");
      await loadState();
    } catch (e) {
      toast.error("Suppression impossible", { description: formatApiError(e?.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  const restart = () => {
    setGenerated({});
    setOrigPreview(null);
    setConsent(false);
    setStep("upload");
  };

  // ---------------------------------------------------------------------
  return (
    <div className="bg-[#FAFAF7] min-h-screen">
      <Navbar variant="light" />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/expert" data-testid="back-expert" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0B3A2A] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour à mon espace
        </Link>

        <div className="flex items-start justify-between gap-6 mb-2">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A961] font-medium mb-2">Espace spécialiste · Studio IA</p>
            <h1 className="font-serif text-3xl md:text-4xl text-[#0B3A2A] tracking-tight">
              Créer ma photo professionnelle
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl leading-relaxed">
              Obtenez en quelques secondes une photo de profil institutionnelle, homogène et conforme aux standards CONFILEX, sans passer par un photographe.
            </p>
          </div>
          {state.published_photo && (
            <div className="shrink-0 text-center">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Photo actuelle</p>
              <img src={state.published_photo} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#C9A961]/50" />
            </div>
          )}
        </div>

        {/* Bandeau RGPD */}
        <div className="mt-6 mb-8 p-4 rounded-lg border border-[#C9A961]/30 bg-[#FAF7F1] text-xs text-[#0B3A2A] leading-relaxed flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 text-[#C9A961] shrink-0" />
          <div>
            <strong>Engagement déontologique :</strong> nos traitements IA modifient uniquement l'arrière-plan, la lumière et la colorimétrie. Aucune modification du visage, de l'âge ou de votre apparence réelle n'est effectuée. Vous validez chaque version avant publication. Vous pouvez supprimer votre photo à tout moment.
          </div>
        </div>

        {/* STEP : UPLOAD */}
        {step === "upload" && !cameraOn && (
          <section className="rounded-2xl bg-white border border-slate-200 p-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="step-upload">
            <button onClick={() => startCamera("photo")} disabled={busy} data-testid="btn-camera"
              className="group p-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#C9A961] hover:bg-[#FAF7F1] transition flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-full bg-[#0B3A2A] flex items-center justify-center mb-3 group-hover:scale-105 transition">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-serif text-base text-[#0B3A2A]">Prendre une photo</h3>
              <p className="text-xs text-slate-500 mt-1">Caméra frontale.</p>
            </button>

            <button onClick={() => fileInputRef.current?.click()} disabled={busy} data-testid="btn-upload"
              className="group p-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#C9A961] hover:bg-[#FAF7F1] transition flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-full bg-[#C9A961] flex items-center justify-center mb-3 group-hover:scale-105 transition">
                <Upload className="w-6 h-6 text-[#0B3A2A]" />
              </div>
              <h3 className="font-serif text-base text-[#0B3A2A]">Importer une photo</h3>
              <p className="text-xs text-slate-500 mt-1">JPG, PNG, WEBP — 8 Mo max.</p>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={onFilePick} className="hidden" data-testid="input-file" />
            </button>

            <button onClick={() => startCamera("video")} disabled={busy} data-testid="btn-record-video"
              className="group p-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#C9A961] hover:bg-[#FAF7F1] transition flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-full bg-[#0B3A2A] flex items-center justify-center mb-3 group-hover:scale-105 transition">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-serif text-base text-[#0B3A2A]">Enregistrer une vidéo</h3>
              <p className="text-xs text-slate-500 mt-1">Présentation 30 s max.</p>
            </button>

            <button onClick={() => videoFileInputRef.current?.click()} disabled={busy} data-testid="btn-upload-video"
              className="group p-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#C9A961] hover:bg-[#FAF7F1] transition flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-full bg-[#C9A961] flex items-center justify-center mb-3 group-hover:scale-105 transition">
                <Upload className="w-6 h-6 text-[#0B3A2A]" />
              </div>
              <h3 className="font-serif text-base text-[#0B3A2A]">Importer une vidéo</h3>
              <p className="text-xs text-slate-500 mt-1">MP4, WebM — 50 Mo max.</p>
              <input ref={videoFileInputRef} type="file" accept="video/mp4,video/webm,video/quicktime"
                onChange={onVideoFilePick} className="hidden" data-testid="input-video-file" />
            </button>
          </section>
        )}

        {/* STEP : CAMERA (photo ou vidéo) */}
        {cameraOn && (
          <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="step-camera">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A961] font-medium">
                {cameraMode === "video" ? "Mode vidéo · 30 s max" : "Mode photo"}
              </p>
              <p className="text-[11px] text-slate-500" data-testid="camera-status">
                {cameraError ? "Erreur" : cameraReady ? "Caméra prête" : "Caméra en cours de chargement..."}
              </p>
            </div>

            <div className="relative max-w-md mx-auto">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full rounded-xl bg-slate-900 aspect-square object-cover"
                style={{ transform: "scaleX(-1)" }}  // miroir pour caméra frontale
                data-testid="video-preview"
              />
              <div className="absolute inset-0 pointer-events-none rounded-xl ring-2 ring-[#C9A961]/40" />
              {!cameraReady && !cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 rounded-xl" data-testid="camera-loading">
                  <div className="text-center text-white">
                    <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
                    <p className="text-sm">Caméra en cours de chargement, veuillez patienter…</p>
                  </div>
                </div>
              )}
              {recording && (
                <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-red-600 text-white text-xs font-medium px-3 py-1 rounded-full" data-testid="recording-badge">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  REC · {recordedDuration}s
                </div>
              )}
            </div>

            {cameraError && (
              <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800" data-testid="camera-error">
                <p className="font-medium mb-1">L'accès caméra n'a pas pu être obtenu.</p>
                <p className="text-xs text-red-700">{cameraError}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => { stopCamera(); startCamera(cameraMode || "photo"); }} data-testid="btn-cam-retry">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Réessayer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { stopCamera(); fileInputRef.current?.click(); }} data-testid="btn-cam-fallback-upload">
                    <Upload className="w-3.5 h-3.5 mr-1" /> Importer un fichier à la place
                  </Button>
                </div>
              </div>
            )}

            {/* Prévisualisation vidéo enregistrée */}
            {recordedBlobUrl && (
              <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200" data-testid="recorded-preview">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Prévisualisation de votre vidéo</p>
                <video src={recordedBlobUrl} controls playsInline className="w-full max-w-md mx-auto rounded-lg bg-slate-900" />
                <p className="text-xs text-slate-500 mt-2 text-center">Durée : {recordedDuration}s</p>
                <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={resetVideoRecording} data-testid="btn-video-reset">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Réenregistrer
                  </Button>
                  <Button size="sm" onClick={uploadRecordedVideo} disabled={busy} data-testid="btn-video-save"
                    className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    Enregistrer dans mon profil
                  </Button>
                </div>
              </div>
            )}

            {/* Contrôles principaux */}
            {!recordedBlobUrl && (
              <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                <Button variant="outline" onClick={stopCamera} data-testid="btn-cam-cancel">
                  <X className="w-4 h-4 mr-1" /> Annuler
                </Button>
                {cameraMode === "photo" && (
                  <Button onClick={capturePhoto} disabled={busy || !cameraReady} data-testid="btn-cam-capture"
                    className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                    Capturer
                  </Button>
                )}
                {cameraMode === "video" && !recording && (
                  <Button onClick={startVideoRecording} disabled={!cameraReady} data-testid="btn-video-start"
                    className="bg-red-600 hover:bg-red-700 text-white">
                    <Camera className="w-4 h-4 mr-2" /> Démarrer l'enregistrement
                  </Button>
                )}
                {cameraMode === "video" && recording && (
                  <Button onClick={stopVideoRecording} data-testid="btn-video-stop"
                    className="bg-slate-700 hover:bg-slate-800 text-white">
                    <X className="w-4 h-4 mr-2" /> Arrêter l'enregistrement
                  </Button>
                )}
              </div>
            )}
          </section>
        )}

        {/* STEP : CHOOSE STYLE */}
        {step === "choose" && (
          <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="step-choose">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="md:col-span-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Votre photo source</p>
                {origPreview && (
                  <img src={origPreview} alt="" className="w-full aspect-square object-cover rounded-xl border border-slate-200" />
                )}
                <Button variant="outline" size="sm" onClick={restart} className="mt-3 w-full" data-testid="btn-reupload">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Changer la photo
                </Button>
              </div>

              <div className="md:col-span-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Choisissez un style</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {STYLES.map(s => (
                    <button key={s.key} onClick={() => generate(s.key)} disabled={generating}
                      data-testid={`style-${s.key}`}
                      className={`text-left rounded-xl border ${selectedStyle === s.key && generating ? "border-[#C9A961] ring-2 ring-[#C9A961]/30" : "border-slate-200 hover:border-[#C9A961]"} bg-white transition p-4 group disabled:opacity-50`}>
                      <div className="flex items-center gap-3">
                        <span
                          className={`w-10 h-10 rounded-lg shrink-0 ${s.border ? "border border-slate-300" : ""}`}
                          style={{ background: s.swatch }}
                        />
                        <div>
                          <p className="font-medium text-[#0B3A2A] text-sm">{s.label}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">{s.desc}</p>
                        </div>
                      </div>
                      {selectedStyle === s.key && generating && (
                        <p className="mt-2 text-xs text-[#C9A961] flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 animate-spin" /> Génération IA en cours…
                        </p>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-4 flex items-start gap-1.5">
                  <Sparkles className="w-3 h-3 text-[#C9A961] mt-0.5" /> Le traitement dure environ 5 à 10 secondes par style.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* STEP : PREVIEW & VALIDATE */}
        {step === "preview" && (
          <section className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="step-preview">
            {Object.keys(generated).length > 0 ? (
              <>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Aperçu avant publication</p>
                <div className="grid md:grid-cols-2 gap-6 items-start">
                  <div>
                    <p className="text-xs text-slate-500 mb-2 font-medium">Original</p>
                    {origPreview ? (
                      <img src={origPreview} alt="" className="w-full aspect-square object-cover rounded-xl border border-slate-200" />
                    ) : (
                      <div className="w-full aspect-square rounded-xl bg-slate-100 flex items-center justify-center text-slate-400">
                        <ImageIcon className="w-10 h-10" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-[#C9A961] mb-2 font-medium flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Résultat IA — {STYLES.find(s => s.key === selectedStyle)?.label}
                    </p>
                    <img src={generated[selectedStyle]} alt="" className="w-full aspect-square object-cover rounded-xl border-2 border-[#C9A961]/40 shadow-lg" data-testid="preview-generated" />
                  </div>
                </div>

                {/* Sélecteur secondaire pour comparer les styles déjà générés */}
                {Object.keys(generated).length > 1 && (
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-500">Comparer :</span>
                    {Object.keys(generated).map(k => (
                      <button key={k} onClick={() => setSelectedStyle(k)}
                        data-testid={`switch-${k}`}
                        className={`px-3 py-1 rounded-full text-xs border ${selectedStyle === k ? "bg-[#0B3A2A] text-white border-[#0B3A2A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#C9A961]"}`}>
                        {STYLES.find(s => s.key === k)?.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Bloc consentement RGPD */}
                <div className="mt-6 p-4 rounded-lg bg-[#FAF7F1] border border-[#C9A961]/40">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                      data-testid="consent-checkbox" className="mt-0.5 w-4 h-4 accent-[#C9A961]" />
                    <span className="text-sm text-[#0B3A2A] leading-relaxed">
                      J'autorise CONFILEX à traiter ma photo afin de générer une photo professionnelle pour mon profil. Je confirme que cette photo est fidèle à mon apparence réelle. Les versions non retenues seront automatiquement supprimées.
                    </span>
                  </label>
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                  <Button variant="outline" onClick={restart} data-testid="btn-restart">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Recommencer
                  </Button>
                  <div className="flex items-center gap-2">
                    {!Object.keys(generated).includes(selectedStyle) && (
                      <Button variant="outline" onClick={() => generate(selectedStyle)} disabled={generating} data-testid="btn-regen">
                        {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                        Régénérer
                      </Button>
                    )}
                    <Button onClick={validate} disabled={!consent || busy}
                      data-testid="btn-validate" className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white">
                      {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                      Publier sur ma fiche
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* Photo déjà publiée — affichage actuel + suppression */
              <div className="text-center py-8" data-testid="published-state">
                {state.published_photo ? (
                  <>
                    <img src={state.published_photo} alt="" className="w-40 h-40 mx-auto rounded-full object-cover border-2 border-[#C9A961]/50 mb-4" />
                    <p className="font-serif text-xl text-[#0B3A2A] mb-2">Votre photo professionnelle est publiée</p>
                    <p className="text-sm text-slate-500 mb-6">Elle apparaît sur votre fiche dans l'orientation CONFILEX.</p>
                    <div className="flex items-center justify-center gap-3">
                      <Button onClick={restart} className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="btn-replace">
                        <Camera className="w-4 h-4 mr-1.5" /> Remplacer la photo
                      </Button>
                      <Button variant="outline" onClick={deletePhoto} disabled={busy} data-testid="btn-delete"
                        className="text-rose-700 border-rose-200 hover:bg-rose-50">
                        <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer ma photo
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-slate-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                    <p>Aucune photo publiée pour le moment.</p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Bandeau de bas de page : interdits */}
        <p className="mt-8 text-[11px] text-slate-500 leading-relaxed max-w-3xl">
          <strong className="text-[#0B3A2A]">Traitements interdits :</strong> modification importante du visage, rajeunissement excessif, transformation trompeuse, création d'une fausse apparence professionnelle. CONFILEX se réserve le droit de retirer toute photo non conforme à ces principes.
        </p>
      </main>
      <Footer />
    </div>
  );
}
